import { KeyboardEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { TFunction } from 'i18next'
import { ChevronDown } from '@digital-twins/design-system/icons'
import {
  SOCIAL_DEPTH_DAYS,
  SOCIAL_FIRST_COLLECTION_POST_LIMITS,
  SOCIAL_RUN_POST_LIMITS,
  SOCIAL_TRACKER_CADENCES,
} from '@digital-twins/shared-types'
import { inputVariants } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Text } from '@/components/ui/Text'
import { cn } from '@/lib/utils'
import type { SocialTrackerScheduleFieldsProps } from '@/types/components/social-tracker-dialog'

interface SchedulePillOption<T> {
  value: T
  label: string
  testId: string
}

interface SchedulePillGroupProps<T> {
  labelledBy: string
  options: Array<SchedulePillOption<T>>
  selected: T
  onSelect: (value: T) => void
}

const NEXT_KEYS = new Set(['ArrowRight', 'ArrowDown'])
const PREVIOUS_KEYS = new Set(['ArrowLeft', 'ArrowUp'])

/**
 * Pills that behave as one radio group: only the selected pill is in the tab
 * order and the arrow keys move the selection, like native radios.
 */
function SchedulePillGroup<T>(props: SchedulePillGroupProps<T>) {
  const { labelledBy, options, selected, onSelect } = props

  const selectedIndex = options.findIndex((option) => option.value === selected)
  const focusableIndex = selectedIndex === -1 ? 0 : selectedIndex

  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    const step = NEXT_KEYS.has(event.key) ? 1 : PREVIOUS_KEYS.has(event.key) ? -1 : 0
    if (step === 0) {
      return
    }
    event.preventDefault()
    const nextIndex = (index + step + options.length) % options.length
    onSelect(options[nextIndex].value)
    const siblings = event.currentTarget.parentElement?.querySelectorAll<HTMLButtonElement>(
      '[role="radio"]',
    )
    siblings?.[nextIndex]?.focus()
  }

  return (
    <div className="flex flex-wrap gap-2" role="radiogroup" aria-labelledby={labelledBy}>
      {options.map((option, index) => {
        const isSelected = option.value === selected
        return (
          <button
            key={option.testId}
            type="button"
            role="radio"
            aria-checked={isSelected}
            tabIndex={index === focusableIndex ? 0 : -1}
            className={cn(
              'inline-flex h-9 items-center rounded-full border px-4 text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-60',
              isSelected
                ? 'border-primary/45 bg-primary/10 text-foreground'
                : 'border-border text-muted-foreground hover:bg-accent hover:text-foreground',
            )}
            data-testid={option.testId}
            onClick={() => onSelect(option.value)}
            onKeyDown={(event) => onKeyDown(event, index)}
          >
            {option.label}
          </button>
        )
      })}
    </div>
  )
}

/** The look-back pill label: "Last 90 days", or "Last year" for 365 days. */
export function socialDepthOptionLabel(t: TFunction<'app'>, days: number): string {
  return days === 365
    ? t('socialListening.form.depthOptionYear')
    : t('socialListening.form.depthOption', { days })
}

/**
 * The schedule in two blocks: "First collection" (how far back the first
 * collection reads and the most posts it keeps per channel) and "Then" (the
 * cadence and the relevant posts a week per channel the updates aim for).
 */
export function SocialTrackerScheduleFields(
  props: SocialTrackerScheduleFieldsProps,
) {
  const {
    depthDays,
    cadence,
    runPostLimit,
    firstCollectionPostLimit,
    isEditing,
    onDepthDaysChange,
    onCadenceChange,
    onRunPostLimitChange,
    onFirstCollectionPostLimitChange,
  } = props

  const { t, i18n } = useTranslation('app')

  return (
    <div
      className="flex flex-col gap-5"
      data-testid="social-tracker-schedule"
    >
      <fieldset className="space-y-1.5" data-testid="social-tracker-first-collection">
        <legend id="social-tracker-depth-legend" className="mb-1.5 text-sm font-medium">
          {t('socialListening.form.firstCollection')}
        </legend>
        <SchedulePillGroup
          labelledBy="social-tracker-depth-legend"
          options={SOCIAL_DEPTH_DAYS.map((days) => ({
            value: days,
            label: socialDepthOptionLabel(t, days),
            testId: `social-tracker-depth-${days}`,
          }))}
          selected={depthDays}
          onSelect={onDepthDaysChange}
        />
        <div className="flex flex-wrap items-center gap-2 pt-1.5">
          <Label htmlFor="social-tracker-first-collection-limit" className="sr-only">
            {t('socialListening.form.firstCollectionLimitLabel')}
          </Label>
          <Text as="span" size="sm" color="muted" aria-hidden="true">
            {t('socialListening.form.firstCollectionUpTo')}
          </Text>
          <div className="relative">
            <select
              id="social-tracker-first-collection-limit"
              value={firstCollectionPostLimit}
              className={cn(inputVariants(), 'w-28 appearance-none pr-9')}
              aria-describedby="social-tracker-first-collection-limit-unit"
              data-testid="social-tracker-first-collection-limit"
              onChange={(event) => {
                const next = SOCIAL_FIRST_COLLECTION_POST_LIMITS.find(
                  (limit) => limit === Number(event.target.value),
                )
                if (next !== undefined) {
                  onFirstCollectionPostLimitChange(next)
                }
              }}
            >
              {SOCIAL_FIRST_COLLECTION_POST_LIMITS.map((limit) => (
                <option key={limit} value={limit}>
                  {limit.toLocaleString(i18n.language)}
                </option>
              ))}
            </select>
            <ChevronDown
              className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
              aria-hidden="true"
            />
          </div>
          <Text
            as="span"
            size="sm"
            color="muted"
            id="social-tracker-first-collection-limit-unit"
          >
            {t('socialListening.form.postsPerChannel')}
          </Text>
        </div>
        {isEditing ? (
          <Text
            as="span"
            size="xs"
            color="muted"
            className="block"
            data-testid="social-tracker-depth-edit-hint"
          >
            {t('socialListening.form.depthEditHint')}
          </Text>
        ) : null}
      </fieldset>

      <fieldset className="flex flex-col gap-3" data-testid="social-tracker-then">
        <legend id="social-tracker-cadence-legend" className="mb-1.5 text-sm font-medium">
          {t('socialListening.form.then')}
        </legend>
        <SchedulePillGroup
          labelledBy="social-tracker-cadence-legend"
          options={SOCIAL_TRACKER_CADENCES.map((value) => ({
            value,
            label: t(`socialListening.form.cadence.${value}`),
            testId: `social-tracker-cadence-${value}`,
          }))}
          selected={cadence}
          onSelect={onCadenceChange}
        />
        <div className="flex flex-wrap items-center gap-2">
          <Label htmlFor="social-tracker-run-post-limit" className="sr-only">
            {t('socialListening.form.weeklyRateLabel')}
          </Label>
          <Text as="span" size="sm" color="muted" aria-hidden="true">
            {t('socialListening.form.firstCollectionUpTo')}
          </Text>
          <div className="relative">
            <select
              id="social-tracker-run-post-limit"
              value={runPostLimit}
              className={cn(inputVariants(), 'w-24 appearance-none pr-9')}
              aria-describedby="social-tracker-run-post-limit-unit"
              data-testid="social-tracker-run-post-limit"
              onChange={(event) => {
                const next = SOCIAL_RUN_POST_LIMITS.find(
                  (limit) => limit === Number(event.target.value),
                )
                if (next !== undefined) {
                  onRunPostLimitChange(next)
                }
              }}
            >
              {SOCIAL_RUN_POST_LIMITS.map((limit) => (
                <option key={limit} value={limit}>
                  {limit}
                </option>
              ))}
            </select>
            <ChevronDown
              className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
              aria-hidden="true"
            />
          </div>
          <Text
            as="span"
            size="sm"
            color="muted"
            id="social-tracker-run-post-limit-unit"
          >
            {t('socialListening.form.relevantPostsPerWeek')}
          </Text>
        </div>
      </fieldset>
    </div>
  )
}
